import React, { useState } from 'react';
import { toast } from 'sonner';
import UserGuide from './UserGuide';
import ContactUs from './ContactUs';

const Login = ({ apiClient, onLogin }) => {
  const [isSignup, setIsSignup] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showUserGuide, setShowUserGuide] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: '',
    address: ''
  });
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Email validation
    if (name === 'email') {
      validateEmail(value);
    }
    
    // Password confirmation validation
    if (name === 'password' || name === 'confirmPassword') {
      if (name === 'password') {
        validatePasswordMatch(value, formData.confirmPassword);
      } else {
        validatePasswordMatch(formData.password, value);
      }
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email && !emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const validatePasswordMatch = (password, confirmPassword) => {
    if (confirmPassword && password !== confirmPassword) {
      setPasswordError('Passwords do not match');
    } else {
      setPasswordError('');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.post('/auth/forgot-password', {
        email: formData.email
      });
      
      toast.success('Password reset email sent! Please check your email inbox.');
      setShowForgotPassword(false);
    } catch (error) {
      console.error('Forgot password error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email before submission
    if (emailError || !formData.email) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    // Validate restaurant name length for signup
    if (isSignup && formData.name && formData.name.length > 25) {
      toast.error('Restaurant name must be 25 characters or less');
      return;
    }
    
    // Validate password match for signup
    if (isSignup && (passwordError || formData.password !== formData.confirmPassword)) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      let endpoint;
      let payload;

      if (isSignup) {
        // Only restaurant signup is available
        endpoint = '/auth/restaurant/signup';
        payload = {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          phone: formData.phone || '', // Optional field
          address: formData.address || '' // Optional field
        };
      } else {
        // For login, first try restaurant login, then admin login if restaurant fails
        try {
          // Try restaurant login first
          endpoint = '/auth/restaurant/login';
          payload = {
            email: formData.email,
            password: formData.password
          };
          
          const response = await apiClient.post(endpoint, payload);
          toast.success('Login successful!');
          onLogin(response.data.access_token, response.data.user_type, response.data.user_id);
          return;
          
        } catch (restaurantError) {
          // If restaurant login fails, try admin login
          if (restaurantError.response?.status === 401) {
            try {
              endpoint = '/auth/admin/login';
              payload = {
                email: formData.email,
                password: formData.password
              };
              
              const response = await apiClient.post(endpoint, payload);
              toast.success('Admin login successful!');
              onLogin(response.data.access_token, response.data.user_type, response.data.user_id);
              return;
              
            } catch (adminError) {
              // Both logins failed, show appropriate error
              if (adminError.response?.status === 401) {
                toast.error('Invalid email or password');
              } else {
                toast.error('Login failed. Please try again.');
              }
              return;
            }
          } else {
            // Restaurant login failed for other reasons
            throw restaurantError;
          }
        }
      }

      const response = await apiClient.post(endpoint, payload);
      toast.success(isSignup ? 'Account created successfully!' : 'Login successful!');
      onLogin(response.data.access_token, response.data.user_type, response.data.user_id);
      
    } catch (error) {
      console.error('Authentication error:', error);
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  if (showForgotPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100">
              <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-6 6c-1.105 0-2.104-.378-2.896-1.005M15 7a2 2 0 00-2 2m2-2v10a6 6 0 01-6 6c-1.105 0-2.104-.378-2.896-1.005M15 7V4a2 2 0 00-2-2H9a2 2 0 00-2 2v3m6 0V7" />
              </svg>
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Forgot Password
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Enter your email to receive password reset instructions
            </p>
          </div>

          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <form className="space-y-6" onSubmit={handleForgotPassword}>
              <div>
                <label htmlFor="email" className="form-label">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  data-testid="forgot-password-email-input"
                  className="form-input"
                  placeholder="Enter your email address"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>

              <div className="flex space-x-4">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="send-reset-token-button"
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? <div className="spinner mx-auto"></div> : 'Reset Password'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  data-testid="cancel-forgot-password-button"
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-gray-100 py-6 sm:py-12 px-3 sm:px-6 lg:px-8">
      {/* User Guide Modal */}
      {showUserGuide && <UserGuide onClose={() => setShowUserGuide(false)} />}
      
      <div className="max-w-md w-full space-y-6 sm:space-y-8">
        {/* Header with User Guide Button */}
        <div className="text-center relative">
          {/* User Guide Button */}
          <button
            onClick={() => setShowUserGuide(true)}
            className="absolute top-0 right-0 bg-white shadow-lg rounded-full p-2 text-blue-600 hover:bg-blue-50 transition-colors group"
            data-testid="user-guide-button"
            title="User Guide"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="sr-only">User Guide</span>
          </button>
          
          <div className="mx-auto h-16 w-16 sm:h-12 sm:w-12 flex items-center justify-center rounded-full bg-blue-500 shadow-lg">
            <span className="text-2xl sm:text-xl">🏪</span>
          </div>
          <h2 className="mt-4 sm:mt-6 text-center text-2xl sm:text-3xl font-extrabold text-gray-900 leading-tight">
            {isSignup ? 'Create Restaurant Account' : 'Sign in to your account'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 font-medium">
            Restaurant QR Ordering System
          </p>
          
          {/* Quick Help Text */}
          <div className="mt-3 flex items-center justify-center space-x-1">
            <span className="text-xs text-gray-500">Need help?</span>
            <button
              onClick={() => setShowUserGuide(true)}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium underline"
              data-testid="help-link"
            >
              View User Guide
            </button>
          </div>
        </div>

        <div className="bg-white py-6 sm:py-8 px-4 sm:px-10 shadow-xl rounded-2xl border border-gray-100">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {isSignup && (
              <div>
                <label htmlFor="name" className="form-label">
                  Restaurant Name
                </label>
                <div className="relative">
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    data-testid="restaurant-name-input"
                    className="form-input"
                    placeholder="Enter restaurant name"
                    maxLength={25}
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-gray-400">
                    {formData.name?.length || 0}/25
                  </div>
                </div>
                {formData.name?.length > 20 && (
                  <p className="text-xs text-orange-500 mt-1">
                    {25 - formData.name.length} characters remaining
                  </p>
                )}
              </div>
            )}

            <div>
              <label htmlFor="email" className="form-label">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                data-testid="email-input"
                className={`form-input ${emailError ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="Enter email address"
                value={formData.email}
                onChange={handleInputChange}
              />
              {emailError && (
                <p className="mt-1 text-xs text-red-500" data-testid="email-error">
                  {emailError}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                data-testid="password-input"
                className="form-input"
                placeholder="Enter password"
                value={formData.password}
                onChange={handleInputChange}
              />
            </div>

            {isSignup && (
              <>
                <div>
                  <label htmlFor="confirmPassword" className="form-label">
                    Confirm Password
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    required
                    data-testid="confirm-password-input"
                    className={`form-input ${passwordError ? 'border-red-500 focus:ring-red-500' : ''}`}
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                  />
                  {passwordError && (
                    <p className="mt-1 text-xs text-red-500" data-testid="password-error">
                      {passwordError}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="phone" className="form-label">
                    Phone Number <span className="text-gray-400 text-xs">(optional)</span>
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    data-testid="phone-input"
                    className="form-input"
                    placeholder="Enter phone number"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </div>

                <div>
                  <label htmlFor="address" className="form-label">
                    Restaurant Address <span className="text-gray-400 text-xs">(optional)</span>
                  </label>
                  <textarea
                    id="address"
                    name="address"
                    rows="3"
                    data-testid="address-input"
                    className="form-input resize-none"
                    placeholder="Enter complete restaurant address"
                    value={formData.address}
                    onChange={handleInputChange}
                  />
                </div>
              </>
            )}

            <div>
              <button
                type="submit"
                disabled={loading}
                data-testid="auth-submit-button"
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="spinner"></div>
                ) : (
                  isSignup ? 'Create Account' : 'Sign in'
                )}
              </button>
            </div>

            {/* Forgot Password Link */}
            {!isSignup && (
              <div className="text-center">
                <button
                  type="button"
                  data-testid="forgot-password-button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-blue-600 hover:text-blue-500"
                >
                  Forgot your password?
                </button>
              </div>
            )}

            <div className="text-center">
              <button
                type="button"
                data-testid="toggle-signup-button"
                onClick={() => setIsSignup(!isSignup)}
                className="text-sm text-blue-600 hover:text-blue-500"
              >
                {isSignup 
                  ? 'Already have an account? Sign in' 
                  : "Don't have an account? Sign up"
                }
              </button>
            </div>
          </form>
          
          {/* Quick Features Overview */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-sm font-semibold text-gray-800 mb-3 text-center">System Features</h3>
            <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
              <div className="flex items-center space-x-2">
                <span className="text-green-500">✓</span>
                <span>QR Code Ordering</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-green-500">✓</span>
                <span>Payment Processing</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-green-500">✓</span>
                <span>Menu Management</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-green-500">✓</span>
                <span>Order Tracking</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-green-500">✓</span>
                <span>Analytics & Reports</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-green-500">✓</span>
                <span>Kitchen Display</span>
              </div>
            </div>
            <div className="mt-3 text-center">
              <button
                onClick={() => setShowUserGuide(true)}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                data-testid="features-guide-link"
              >
                Learn more about all features →
              </button>
            </div>
          </div>
        </div>
        
        {/* Contact Us Section */}
        <ContactUs apiClient={apiClient} />
        
        {/* Disclaimer Section */}
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-xs font-semibold text-gray-700">Disclaimer</h3>
          </div>
          <p className="text-xs text-gray-600 leading-relaxed">
            By using this restaurant ordering system, you acknowledge that your use is entirely at your own risk. 
            We make no warranties or representations regarding the accuracy, reliability, or availability of this service. 
            We shall not be liable for any direct, indirect, incidental, or consequential damages or losses arising from 
            your use of this website or system, including but not limited to business interruption, data loss, or financial losses.
          </p>
          <div className="mt-2 pt-2 border-t border-gray-300">
            <p className="text-xs text-gray-500">
              © {new Date().getFullYear()} Restaurant QR Ordering System. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;